import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import { movies } from "@/mocks/movies";

const PAGE_SIZE = 8;

const genreOptions = ["全部", "剧情", "爱情", "动作", "喜剧"];
const eraOptions = ["全部", "1990-1994", "1995-1997"];
const regionOptions = ["全部", "香港", "台湾"];
const sortOptions = [
  { value: "popular", label: "热门排序" },
  { value: "newest", label: "最新上映" },
];

function resolveEra(year: number): string {
  if (year <= 1994) return "1990-1994";
  if (year <= 1997) return "1995-1997";
  return "1998-2000";
}

export default function CategoryPage() {
  useSeo({
    title: "电影分类 - 蜜桃成熟时1997影视内容站",
    description:
      "按类型、年代与地区筛选九十年代经典亚洲电影，支持热门与最新排序，快速找到你感兴趣的影片并查看详细介绍与剧情解析。",
    keywords: "电影分类,90年代电影,亚洲电影,香港电影,经典电影,蜜桃成熟时1997",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get("tag") || "全部";

  const [genre, setGenre] = useState("全部");
  const [era, setEra] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [sort, setSort] = useState("popular");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    const list = movies.filter((movie) => {
      const matchTag = activeTag === "全部" || movie.tags.includes(activeTag);
      const matchGenre = genre === "全部" || movie.genre === genre;
      const matchEra = era === "全部" || resolveEra(movie.year) === era;
      const matchRegion = region === "全部" || movie.region === region;
      return matchTag && matchGenre && matchEra && matchRegion;
    });
    return list.sort((a, b) =>
      sort === "newest" ? b.year - a.year : b.popularity - a.popularity,
    );
  }, [activeTag, genre, era, region, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const update = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const resetAll = () => {
    setGenre("全部");
    setEra("全部");
    setRegion("全部");
    setSort("popular");
    setPage(1);
    setSearchParams({});
  };

  const filterGroup = (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">类型</p>
        <div className="flex flex-wrap gap-2">
          {genreOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => update(setGenre)(option)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                genre === option
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">年代</p>
        <div className="flex flex-wrap gap-2">
          {eraOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => update(setEra)(option)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                era === option
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">地区</p>
        <div className="flex flex-wrap gap-2">
          {regionOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => update(setRegion)(option)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                region === option
                  ? "bg-secondary-600 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">排序</p>
        <div className="px-1 py-1 rounded-full bg-background-100 inline-flex gap-1">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update(setSort)(option.value)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === option.value
                  ? "bg-background-50 text-foreground-950 shadow-sm"
                  : "text-foreground-600"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />
        <div className="mt-5 mb-8 md:mb-10">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            电影分类
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            这里收录了九十年代经典亚洲电影，涵盖剧情、爱情、动作与喜剧等类型。你可以按类型、年代与地区筛选，并按热门或上映时间排序，快速找到想了解的作品。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 pb-16">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-600"></i>
                  筛选
                </h2>
                <button
                  type="button"
                  onClick={resetAll}
                  className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  重置
                </button>
              </div>
              {filterGroup}
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 部影片
                {activeTag !== "全部" && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs">
                    {activeTag}
                  </span>
                )}
              </p>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
            </div>

            {paged.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {paged.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-50 py-20 text-center">
                <span className="mx-auto w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  没有找到符合条件的影片
                </h3>
                <p className="mt-2 text-sm text-foreground-500">
                  换个筛选条件试试，或重置所有筛选。
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="relative w-full bg-background-50 rounded-t-2xl max-h-[80vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-background-100 flex items-center justify-center text-foreground-700 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {filterGroup}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={resetAll}
                className="flex-1 px-4 py-3 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap"
              >
                查看 {filtered.length} 部影片
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}